"use client";

import logger from "../../utils/logger";
import { WebViewerInstance } from "@pdftron/webviewer";
import { useEffect, useState, useRef } from "react";
import { usePubSub, useMeeting } from "@videosdk.live/react-sdk";
import { useSiteContext } from "../../context/SiteContext";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import useIsMobile from "../../hooks/useIsMobile";

export default function DocumentViewer({
  fileUrl,
  onClosePanel,
  sideBarContainerWidth,
}) {
  const { sideBarMode, leftSideBarMode } = useMeetingAppContext();
  const viewer = useRef(null);
  const webviewerInstanceRef = useRef(null);
  const [webviewerInstance, setWebViewerInstance] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const isApplyingRemoteChange = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const observerRef = useRef(null);
  const resizeHandlerRef = useRef(null);
  const isDocumentLoaded = useRef(false);
  const queuedAnnotations = useRef(null);
  const isPageNavigating = useRef(false); // Flag to allow scroll during page navigation
  // On mobile we must let FitWidth set zoom, then lock it to avoid clipping.
  const lockedZoomLevelRef = useRef(null);
  const { meetingRole } = useSiteContext();
  const isMobile = useIsMobile();

  const getIsMobileView = () => {
    if (isMobile) {
      return true;
    }
    if (typeof window !== "undefined") {
      return window.innerWidth <= 767;
    }
    return false;
  };

  const mMeeting = useMeeting();

  const { publish } = usePubSub("SHOW_DOCUMENT_SECTION", {
    onMessageReceived: (data) => {
      const instance = webviewerInstanceRef.current;
      if (!instance || !data?.message) return;

      const rawMessage = data.message || data;
      const messageData =
        typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      // Don't process our own events
      if (data.senderId === mMeeting?.localParticipant?.id) {
        return;
      }

      logger("log", "Received SHOW_DOCUMENT_SECTION event:", data);

      const { documentViewer } = instance.Core;
      const scrollView = documentViewer.getScrollViewElement();
      const { eventName, eventData } = messageData;

      // Set flag to prevent feedback loop
      isApplyingRemoteChange.current = true;

      try {
        switch (eventName) {
          case "pageNumberUpdated":
            if (
              eventData?.pageNumber &&
              documentViewer.getCurrentPage() !== eventData.pageNumber
            ) {
              // Allow scroll during remote page navigation
              isPageNavigating.current = true;
              documentViewer.setCurrentPage(eventData.pageNumber);
              // Reset flag after navigation completes
              setTimeout(() => {
                isPageNavigating.current = false;
              }, 600);
            }
            break;

          case "zoomUpdated":
            // DISABLED - Zoom events are disabled
            // if (eventData?.zoom) {
            //   instance.UI.setZoomLevel(eventData.zoom);
            // }
            break;

          case "scrollUpdated":
            // DISABLED - Scroll events are disabled
            // if (
            //   eventData?.scrollTop !== undefined &&
            //   eventData?.scrollLeft !== undefined
            // ) {
            //   scrollView.scrollTop = eventData.scrollTop;
            //   scrollView.scrollLeft = eventData.scrollLeft;
            // }
            break;

          case "fitModeChanged":
            if (eventData?.fitMode) {
              instance.UI.setFitMode(eventData.fitMode);
            }
            break;

          case "annotationChanged":
            if (eventData?.annotations) {
              const { annotationManager: annManager, documentViewer } =
                instance.Core;
              // Check if document is loaded before importing annotations
              if (
                isDocumentLoaded.current &&
                documentViewer.getPageCount() > 0
              ) {
                try {
                  annManager.importAnnotations(eventData.annotations);
                } catch (error) {
                  logger("error", "Error importing annotations:", error);
                }
              } else {
                // Queue annotations to import after document loads
                queuedAnnotations.current = eventData.annotations;
              }
            }
            break;

          case "layoutChanged":
            if (eventData?.layoutMode) {
              instance.UI.setLayoutMode(eventData.layoutMode);
            }
            break;

          case "toolModeUpdated":
            if (eventData?.toolName) {
              instance.UI.setToolMode(eventData.toolName);
            }
            break;

          case "rotationUpdated":
            logger("log", "rotationUpdated", instance);
            if (eventData?.rotation && eventData?.pageNumber) {
              // do for all pages
              for (let i = 1; i <= documentViewer.getPageCount(); i++) {
                const originalRotation = documentViewer
                  .getDocument()
                  .getPageRotation(i);
                if (originalRotation !== eventData.rotation) {
                  documentViewer
                    .getDocument()
                    .rotatePages([i], eventData.rotation * 90);
                }
              }
              documentViewer.refreshAll();
              documentViewer.updateView();
            }
            break;

          default:
            logger("log", "Unknown event:", eventName);
        }
      } finally {
        // Reset flag after a delay to allow event to complete and prevent feedback
        setTimeout(() => {
          isApplyingRemoteChange.current = false;
        }, 500); // Increased delay for rotation/redraw
      }
    },
  });

  const sanitizeUrl = (url) => {
    if (url.startsWith("http://")) {
      return url.replace("http://", "https://");
    } else if (url.startsWith("https://")) {
      return url;
    } else {
      return `https://${url}`;
    }
  };

  useEffect(() => {
    // Reset document loaded flag when fileUrl changes
    isDocumentLoaded.current = false;
    queuedAnnotations.current = null;

    // Due to SSR in Next.js, we need to import the module dynamically, to avoid window is not defined error due to re-rendering.
    // Read more here: https://github.com/vercel/next.js/discussions/42319
    import("@pdftron/webviewer").then((module) => {
      const WebViewer = module.default;
      WebViewer(
        {
          path: "/lib/webviewer",
          initialDoc: sanitizeUrl(fileUrl),
          licenseKey:
            "R-Link LLC:OEM:a5jOK000000xArN::B+3:AMS(20260828):A773575F761D1145BB535788D2C8F496839C21EDF969F948792B170966FA656D", // sign up to get a free trial key at https://dev.apryse.com
          disableVirtualDisplayMode: true,
          initialLayoutMode: "Single",
          // Mobile should NOT be scrollable; use single-page mode.
          initialDisplayMode: getIsMobileView() ? "Single" : "Continuous",
          width: "100%",
          enableAnnotations: true,
          enableFilePicker: false,
          enableMeasurement: false,
        },
        viewer.current,
      ).then((instance) => {
        const { documentViewer, annotationManager, Annotations } =
          instance.Core;
        // Run APIs here.

        // Define disableScrolling function to prevent all scrolling
        const disableScrolling = () => {
          try {
            const scrollView = documentViewer.getScrollViewElement();
            if (scrollView) {
              // Prevent scrolling via CSS - completely lock scroll position
              scrollView.style.overflow = "hidden";
              scrollView.style.overflowY = "hidden";
              scrollView.style.overflowX = "hidden";
              scrollView.style.scrollBehavior = "auto";

              // Store last known "good" scroll position
              // Don't force to (0,0) - let PDFTron determine the natural position for each page
              let lastGoodScrollTop = scrollView.scrollTop || 0;
              let lastGoodScrollLeft = scrollView.scrollLeft || 0;
              let scrollPositionStable = false; // Track when scroll position has stabilized

              // Lock scroll position - but only prevent user-initiated scrolling
              const lockScrollPosition = () => {
                // Don't lock scroll if we're navigating pages (allow page positioning)
                if (isPageNavigating.current) {
                  // Wait for scroll position to stabilize during navigation
                  scrollPositionStable = false;
                  return;
                }

                // After navigation completes, allow a moment for position to stabilize
                if (!scrollPositionStable) {
                  // Update good position - PDFTron has positioned the page
                  const currentTop = scrollView.scrollTop;
                  const currentLeft = scrollView.scrollLeft;

                  // Check if position is stable (not changing rapidly)
                  if (
                    Math.abs(currentTop - lastGoodScrollTop) < 1 &&
                    Math.abs(currentLeft - lastGoodScrollLeft) < 1
                  ) {
                    scrollPositionStable = true;
                    lastGoodScrollTop = currentTop;
                    lastGoodScrollLeft = currentLeft;
                  } else {
                    lastGoodScrollTop = currentTop;
                    lastGoodScrollLeft = currentLeft;
                  }
                  return;
                }

                // Only reset scroll if it changed significantly (user tried to scroll)
                // Allow small changes (1px) for PDFTron's internal adjustments
                const topDiff = Math.abs(
                  scrollView.scrollTop - lastGoodScrollTop,
                );
                const leftDiff = Math.abs(
                  scrollView.scrollLeft - lastGoodScrollLeft,
                );

                if (topDiff > 1 || leftDiff > 1) {
                  scrollView.scrollTop = lastGoodScrollTop;
                  scrollView.scrollLeft = lastGoodScrollLeft;
                }
              };

              // Continuously monitor and prevent user scrolling (but allow during page nav)
              const scrollLockInterval = setInterval(() => {
                lockScrollPosition();
              }, 50);

              // Store interval for cleanup
              if (!window.documentViewerScrollLocks) {
                window.documentViewerScrollLocks = [];
              }
              window.documentViewerScrollLocks.push(scrollLockInterval);

              // Prevent wheel scrolling - block ALL wheel events (including zoom attempts)
              const preventAllWheel = (e) => {
                // Prevent ALL wheel events - both scrolling and zoom
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                // Lock scroll position immediately when wheel event occurs
                lockScrollPosition();
                return false;
              };

              scrollView.addEventListener("wheel", preventAllWheel, {
                passive: false,
                capture: true,
              });

              // Prevent touch scrolling - block ALL touch events
              scrollView.addEventListener(
                "touchmove",
                (e) => {
                  // Prevent ALL touch scrolling and zoom gestures
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  return false;
                },
                { passive: false, capture: true },
              );

              // Also prevent touchstart to block gesture initiation
              scrollView.addEventListener(
                "touchstart",
                (e) => {
                  // Block multi-touch gestures (pinch zoom) immediately
                  if (e.touches && e.touches.length >= 2) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                  }
                },
                { passive: false, capture: true },
              );

              // Prevent keyboard scrolling
              scrollView.addEventListener("keydown", (e) => {
                if (
                  [
                    "ArrowUp",
                    "ArrowDown",
                    "PageUp",
                    "PageDown",
                    "Home",
                    "End",
                    " ",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              });
            }
          } catch (e) {
            logger("log", "Could not disable scrolling:", e);
          }
        };

        // Define disableZoom function to prevent all zooming
        const disableZoom = () => {
          try {
            // Lock zoom level (prefer the one captured after FitWidth on mobile)
            if (lockedZoomLevelRef.current == null) {
              try {
                lockedZoomLevelRef.current = instance.UI.getZoomLevel() || 1;
              } catch (e) {
                lockedZoomLevelRef.current = 1;
              }
            }
            const getLockedZoom = () => lockedZoomLevelRef.current ?? 1;

            // Override zoom methods FIRST to prevent any zoom changes
            const originalSetZoomLevel = instance.UI.setZoomLevel;
            if (
              originalSetZoomLevel &&
              typeof originalSetZoomLevel === "function"
            ) {
              instance.UI.setZoomLevel = function (level) {
                // Always reset to locked zoom level - prevent any zoom changes
                try {
                  originalSetZoomLevel.call(instance.UI, getLockedZoom());
                } catch (e) {
                  logger("log", "Could not reset zoom level:", e);
                }
              };
            }

            // Override zoomIn method if it exists
            if (
              instance.UI.zoomIn &&
              typeof instance.UI.zoomIn === "function"
            ) {
              const originalZoomIn = instance.UI.zoomIn;
              instance.UI.zoomIn = function () {
                // Prevent zoom in
                try {
                  originalSetZoomLevel.call(instance.UI, getLockedZoom());
                } catch (e) {}
              };
            }

            // Override zoomOut method if it exists
            if (
              instance.UI.zoomOut &&
              typeof instance.UI.zoomOut === "function"
            ) {
              const originalZoomOut = instance.UI.zoomOut;
              instance.UI.zoomOut = function () {
                // Prevent zoom out
                try {
                  originalSetZoomLevel.call(instance.UI, getLockedZoom());
                } catch (e) {}
              };
            }

            // Disable zoom via UI controls
            if (instance.UI.setToolMode) {
              // Ensure zoom tool is not active
              instance.UI.setToolMode(instance.UI.ToolMode.Pan);
            }

            // Get all elements that could trigger zoom
            const scrollView = documentViewer.getScrollViewElement();
            const viewerElement = viewer.current;

            // Prevent zoom via mouse wheel (ALL wheel events) - also prevent scrolling
            const preventZoomWheel = (e) => {
              // Prevent ALL wheel events - completely disable zoom AND scroll via wheel
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              // Also prevent default behavior to stop scrolling
              if (e.preventDefault) {
                e.preventDefault();
              }
              return false;
            };

            // Prevent pinch-to-zoom gestures - block ALL multi-touch
            const preventZoomTouch = (e) => {
              // Block all multi-touch gestures (pinch zoom)
              if (e.touches && e.touches.length >= 2) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
              }
              // Block touch move that might trigger scrolling when zoom is attempted
              if (e.type === "touchmove") {
                // If Ctrl/Cmd is pressed (zoom attempt), prevent both zoom and scroll
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  return false;
                }
                // Check if this is part of a zoom gesture by checking touch.target
                if (
                  e.target &&
                  (e.target.closest('[data-element*="zoom"]') ||
                    e.target.closest(".zoom"))
                ) {
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  return false;
                }
              }
            };

            // Prevent keyboard zoom shortcuts (Ctrl/Cmd + +/-, Ctrl/Cmd + 0, and all number keys)
            const preventZoomKeyboard = (e) => {
              // Block all zoom-related keyboard shortcuts
              if (
                (e.ctrlKey || e.metaKey) &&
                (e.key === "+" ||
                  e.key === "-" ||
                  e.key === "=" ||
                  e.key === "0" ||
                  e.key === "9" ||
                  e.code === "Equal" ||
                  e.code === "Minus" ||
                  e.code === "Digit0" ||
                  e.code === "Digit9" ||
                  e.code === "NumpadAdd" ||
                  e.code === "NumpadSubtract")
              ) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
              }
            };

            // Add event listeners to scrollView - prevent both zoom and scroll
            if (scrollView) {
              // Block ALL wheel events - prevents both zoom and scroll
              scrollView.addEventListener("wheel", preventZoomWheel, {
                passive: false,
                capture: true,
              });
              // Also add a direct wheel handler to ensure no scroll happens
              scrollView.addEventListener(
                "wheel",
                (e) => {
                  // Double-check: prevent any wheel-based scrolling
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                  return false;
                },
                {
                  passive: false,
                  capture: false, // Use bubble phase as backup
                },
              );

              scrollView.addEventListener("touchstart", preventZoomTouch, {
                passive: false,
                capture: true,
              });
              scrollView.addEventListener("touchmove", preventZoomTouch, {
                passive: false,
                capture: true,
              });
              scrollView.addEventListener("touchend", preventZoomTouch, {
                passive: false,
                capture: true,
              });
              scrollView.addEventListener("keydown", preventZoomKeyboard, {
                passive: false,
                capture: true,
              });
              scrollView.addEventListener("keyup", preventZoomKeyboard, {
                passive: false,
                capture: true,
              });
            }

            // Add event listeners to viewer element
            if (viewerElement) {
              viewerElement.addEventListener("wheel", preventZoomWheel, {
                passive: false,
                capture: true,
              });
              viewerElement.addEventListener("touchstart", preventZoomTouch, {
                passive: false,
                capture: true,
              });
              viewerElement.addEventListener("touchmove", preventZoomTouch, {
                passive: false,
                capture: true,
              });
              viewerElement.addEventListener("touchend", preventZoomTouch, {
                passive: false,
                capture: true,
              });
              viewerElement.addEventListener("keydown", preventZoomKeyboard, {
                passive: false,
                capture: true,
              });
              viewerElement.addEventListener("keyup", preventZoomKeyboard, {
                passive: false,
                capture: true,
              });
            }

            // Add window-level event listeners to catch zoom at document level
            const preventWindowZoom = (e) => {
              if (
                (e.ctrlKey || e.metaKey) &&
                (e.key === "+" ||
                  e.key === "-" ||
                  e.key === "=" ||
                  e.key === "0" ||
                  e.code === "Equal" ||
                  e.code === "Minus" ||
                  e.code === "Digit0")
              ) {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }
            };
            window.addEventListener("keydown", preventWindowZoom, {
              passive: false,
              capture: true,
            });

            // Disable zoom in/out buttons if they exist
            if (viewerElement) {
              const disableZoomButtons = () => {
                const zoomInElements = viewerElement.querySelectorAll(
                  '[data-element*="zoomIn"], [data-element*="zoom-in"], [data-element*="ZoomIn"], button[title*="Zoom In"], button[title*="zoom in"], .zoom-in, .ZoomIn',
                );
                const zoomOutElements = viewerElement.querySelectorAll(
                  '[data-element*="zoomOut"], [data-element*="zoom-out"], [data-element*="ZoomOut"], button[title*="Zoom Out"], button[title*="zoom out"], .zoom-out, .ZoomOut',
                );
                [...zoomInElements, ...zoomOutElements].forEach((el) => {
                  el.style.pointerEvents = "none";
                  el.style.opacity = "0.3";
                  el.style.cursor = "not-allowed";
                  el.disabled = true;
                  // Remove click handlers
                  el.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  };
                });
              };

              // Disable buttons immediately and on DOM changes
              disableZoomButtons();
              setTimeout(disableZoomButtons, 100);
              setTimeout(disableZoomButtons, 500);
              setTimeout(disableZoomButtons, 1000);

              // Use MutationObserver to disable any dynamically added zoom buttons
              const zoomObserver = new MutationObserver(() => {
                disableZoomButtons();
              });
              zoomObserver.observe(viewerElement, {
                childList: true,
                subtree: true,
                attributes: true,
              });
            }

            // Also override documentViewer zoom methods if they exist
            try {
              if (
                documentViewer.setZoomLevel &&
                typeof documentViewer.setZoomLevel === "function"
              ) {
                const originalDocSetZoom =
                  documentViewer.setZoomLevel.bind(documentViewer);
                documentViewer.setZoomLevel = function (level) {
                  // Always reset to locked zoom level
                  try {
                    originalDocSetZoom(getLockedZoom());
                  } catch (e) {}
                };
              }
            } catch (e) {
              logger(
                "log",
                "Could not override documentViewer zoom methods:",
                e,
              );
            }

            // Set up continuous zoom monitoring to catch any zoom changes
            const zoomMonitor = setInterval(() => {
              try {
                const currentZoom = instance.UI.getZoomLevel();
                if (
                  currentZoom &&
                  lockedZoomLevelRef.current != null &&
                  Math.abs(currentZoom - getLockedZoom()) > 0.001
                ) {
                  // Zoom level changed, reset it
                  const originalSetZoomLevel = instance.UI.setZoomLevel;
                  if (originalSetZoomLevel) {
                    originalSetZoomLevel.call(instance.UI, getLockedZoom());
                  }
                }
              } catch (e) {
                // Ignore monitoring errors
              }
            }, 100); // Check every 100ms

            // Store interval ID for cleanup
            if (!window.documentViewerZoomMonitors) {
              window.documentViewerZoomMonitors = [];
            }
            window.documentViewerZoomMonitors.push(zoomMonitor);
          } catch (e) {
            logger("log", "Could not disable zoom:", e);
          }
        };

        // Define hideToolbars function first so it's accessible everywhere
        const hideToolbars = () => {
          const viewerElement = viewer.current;
          if (viewerElement) {
            // Function to hide element and all children
            const hideElement = (el) => {
              if (el) {
                el.style.cssText =
                  "display: none !important; visibility: hidden !important; height: 0 !important; max-height: 0 !important; overflow: hidden !important; padding: 0 !important; margin: 0 !important;";
                el.setAttribute("hidden", "true");
                el.setAttribute("aria-hidden", "true");
              }
            };

            // Check shadow DOM
            const checkShadowDOM = (element) => {
              if (element && element.shadowRoot) {
                const shadowElements = element.shadowRoot.querySelectorAll("*");
                shadowElements.forEach((el) => {
                  const className = el.className?.toString() || "";
                  const id = el.id || "";
                  const dataElement = el.getAttribute("data-element") || "";
                  const tagName = el.tagName?.toLowerCase() || "";

                  if (
                    className.toLowerCase().includes("toolbar") ||
                    className.toLowerCase().includes("header") ||
                    className.toLowerCase().includes("ribbon") ||
                    id.toLowerCase().includes("toolbar") ||
                    id.toLowerCase().includes("header") ||
                    dataElement.toLowerCase().includes("toolbar") ||
                    dataElement.toLowerCase().includes("header") ||
                    tagName === "header" ||
                    tagName === "nav"
                  ) {
                    hideElement(el);
                  }
                });
              }
            };

            // Check all elements for shadow DOM
            const allElementsForShadow = viewerElement.querySelectorAll("*");
            allElementsForShadow.forEach((el) => {
              checkShadowDOM(el);
            });
            checkShadowDOM(viewerElement);

            // Also check iframes if WebViewer uses iframe
            const iframes = viewerElement.querySelectorAll("iframe");
            iframes.forEach((iframe) => {
              try {
                const iframeDoc =
                  iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc) {
                  // Check shadow DOM in iframe
                  checkShadowDOM(iframeDoc.documentElement);

                  const iframeElements = iframeDoc.querySelectorAll("*");
                  iframeElements.forEach((el) => {
                    checkShadowDOM(el);
                    const className = el.className?.toString() || "";
                    const id = el.id || "";
                    const dataElement = el.getAttribute("data-element") || "";
                    const tagName = el.tagName?.toLowerCase() || "";

                    if (
                      className.toLowerCase().includes("toolbar") ||
                      className.toLowerCase().includes("header") ||
                      className.toLowerCase().includes("ribbon") ||
                      id.toLowerCase().includes("toolbar") ||
                      id.toLowerCase().includes("header") ||
                      dataElement.toLowerCase().includes("toolbar") ||
                      dataElement.toLowerCase().includes("header") ||
                      tagName === "header" ||
                      tagName === "nav"
                    ) {
                      hideElement(el);
                    }
                  });
                }
              } catch (e) {
                // Cross-origin iframe, can't access
              }
            });
            // Hide all toolbar-related elements with comprehensive selectors
            const toolbarSelectors = [
              ".Toolbar",
              ".toolbar",
              ".Header",
              ".header",
              ".DocumentHeader",
              ".Ribbon",
              ".ribbon",
              ".ToolsHeader",
              ".toolsHeader",
              ".ToolbarGroup",
              ".toolbar-group",
              '[data-element*="toolbar"]',
              '[data-element*="Toolbar"]',
              '[data-element="documentHeader"]',
              '[data-element="toolsHeader"]',
              '[data-element="header"]',
              '[data-element="ribbon"]',
              '[class*="Toolbar"]',
              '[class*="toolbar"]',
              '[class*="Header"]',
              '[class*="header"]',
              '[id*="toolbar"]',
              '[id*="Toolbar"]',
              '[id*="header"]',
              '[id*="Header"]',
              'div[class*="Toolbar"]',
              'div[class*="toolbar"]',
              'div[class*="Header"]',
              'nav[class*="Toolbar"]',
              'nav[class*="toolbar"]',
            ];

            toolbarSelectors.forEach((selector) => {
              try {
                const elements = viewerElement.querySelectorAll(selector);
                elements.forEach((el) => {
                  hideElement(el);
                });
              } catch (e) {
                // Ignore selector errors
              }
            });

            // Also try to find and hide by inspecting all child elements
            // But preserve navigation elements
            const allElements = viewerElement.querySelectorAll("*");
            allElements.forEach((el) => {
              checkShadowDOM(el);
              const className = el.className?.toString() || "";
              const id = el.id || "";
              const dataElement = el.getAttribute("data-element") || "";
              const tagName = el.tagName?.toLowerCase() || "";

              // Don't hide navigation/page controls
              const isNavigation =
                dataElement.toLowerCase().includes("navigation") ||
                dataElement.toLowerCase().includes("page") ||
                dataElement.toLowerCase().includes("prev") ||
                dataElement.toLowerCase().includes("next") ||
                className.toLowerCase().includes("navigation") ||
                className.toLowerCase().includes("pagecontrol") ||
                className.toLowerCase().includes("page-control");

              if (
                !isNavigation &&
                (className.toLowerCase().includes("toolbar") ||
                  className.toLowerCase().includes("header") ||
                  className.toLowerCase().includes("ribbon") ||
                  id.toLowerCase().includes("toolbar") ||
                  id.toLowerCase().includes("header") ||
                  (dataElement.toLowerCase().includes("toolbar") &&
                    !dataElement.toLowerCase().includes("navigation")) ||
                  (dataElement.toLowerCase().includes("header") &&
                    !dataElement.toLowerCase().includes("page")) ||
                  tagName === "header" ||
                  (tagName === "nav" && !isNavigation))
              ) {
                hideElement(el);
              }
            });

            // Try to access WebViewer's internal DOM structure
            try {
              // Check if instance has a getViewerElement or similar method
              if (instance.UI.getViewerElement) {
                const viewerEl = instance.UI.getViewerElement();
                if (viewerEl) {
                  checkShadowDOM(viewerEl);
                  const viewerChildren = viewerEl.querySelectorAll("*");
                  viewerChildren.forEach((el) => {
                    const className = el.className?.toString() || "";
                    const id = el.id || "";
                    if (
                      className.toLowerCase().includes("toolbar") ||
                      className.toLowerCase().includes("header") ||
                      id.toLowerCase().includes("toolbar") ||
                      id.toLowerCase().includes("header")
                    ) {
                      hideElement(el);
                    }
                  });
                }
              }
            } catch (e) {
              // Method not available
            }

            // Debug: Log all elements to help identify toolbar
            logger("log", "WebViewer elements:", {
              total: viewerElement.querySelectorAll("*").length,
              iframes: viewerElement.querySelectorAll("iframe").length,
              headers: viewerElement.querySelectorAll("header").length,
              navs: viewerElement.querySelectorAll("nav").length,
            });
          }
        };

        documentViewer.addEventListener("documentLoaded", () => {
          // Mark document as loaded
          isDocumentLoaded.current = true;

          // Force single page mode on mobile immediately
          if (getIsMobileView()) {
            try {
              instance.UI.setLayoutMode(instance.UI.LayoutMode.Single);
              instance.UI.setDisplayMode(instance.UI.DisplayMode.Single);
              documentViewer.updateView();

              // Force single column layout via CSS
              setTimeout(() => {
                const viewerElement = viewer.current;
                if (viewerElement) {
                  const containers = viewerElement.querySelectorAll("div");
                  containers.forEach((container) => {
                    const computedStyle = window.getComputedStyle(container);
                    if (
                      computedStyle.columns &&
                      parseInt(computedStyle.columns) > 1
                    ) {
                      container.style.columns = "1";
                      container.style.columnCount = "1";
                    }
                    if (
                      computedStyle.display === "grid" ||
                      computedStyle.display === "flex"
                    ) {
                      container.style.display = "block";
                      container.style.flexDirection = "column";
                    }
                  });
                }
              }, 100);
            } catch (e) {
              console.log("Error setting single page mode:", e);
            }
          }

          // Apply FitMode first; then lock scroll/zoom.
          // On mobile: capture zoom AFTER FitWidth so content doesn't clip.
          // On desktop: keep original locking behavior.
          setTimeout(() => {
            try {
              const fitMode = getIsMobileView()
                ? instance.UI.FitMode.FitWidth
                : instance.UI.FitMode.FitPage;
              instance.UI.setFitMode(fitMode);
              documentViewer.updateView();
            } catch (e) {}

            setTimeout(
              () => {
                try {
                  lockedZoomLevelRef.current = instance.UI.getZoomLevel() || 1;
                } catch (e) {
                  lockedZoomLevelRef.current = 1;
                }
                disableScrolling();
                disableZoom();

                // Force single page mode again after view updates
                if (getIsMobileView()) {
                  try {
                    instance.UI.setLayoutMode(instance.UI.LayoutMode.Single);
                    instance.UI.setDisplayMode(instance.UI.DisplayMode.Single);
                    documentViewer.updateView();
                  } catch (e) {
                    console.log("Error enforcing single page mode:", e);
                  }
                }
              },
              getIsMobileView() ? 250 : 50,
            );
          }, 50);

          // Process any queued annotations
          if (queuedAnnotations.current) {
            try {
              const { annotationManager: annManager } = instance.Core;
              annManager.importAnnotations(queuedAnnotations.current);
              queuedAnnotations.current = null;
            } catch (error) {
              logger("error", "Error importing queued annotations:", error);
            }
          }

          // Set total pages - with delay for PPTX files
          const updatePageCount = () => {
            try {
              const pageCount = documentViewer.getPageCount();
              if (pageCount > 0) {
                setTotalPages(pageCount);
                setCurrentPage(1);
                logger("log", "Page count updated:", pageCount);
              } else {
                // Retry if page count is 0 (common with PPTX)
                setTimeout(updatePageCount, 500);
              }
            } catch (e) {
              logger("error", "Error getting page count:", e);
              // Retry after delay
              setTimeout(updatePageCount, 500);
            }
          };

          // Try immediately and with delays for PPTX files
          updatePageCount();
          setTimeout(updatePageCount, 100);
          setTimeout(updatePageCount, 500);
          setTimeout(updatePageCount, 1000);

          // Default rectangle annotation removed - no longer creating automatic square
          // const rectangleAnnot = new Annotations.RectangleAnnotation({
          //   PageNumber: 1,
          //   // values are in page coordinates with (0, 0) in the top left
          //   X: 100,
          //   Y: 150,
          //   Width: 200,
          //   Height: 50,
          //   Author: annotationManager.getCurrentUser(),
          // });
          // annotationManager.addAnnotation(rectangleAnnot);
          // // need to draw the annotation otherwise it won't show up until the page is refreshed
          // annotationManager.redrawAnnotation(rectangleAnnot);
          // disable insert edit

          // Hide toolbars when document loads
          setTimeout(() => {
            hideToolbars();
          }, 100);

          // Ensure display mode after document loads
          setTimeout(() => {
            try {
              if (instance.UI.setDisplayMode) {
                instance.UI.setDisplayMode(
                  getIsMobileView()
                    ? instance.UI.DisplayMode.Single
                    : instance.UI.DisplayMode.Continuous,
                );
              }
              // Fit mode is handled above (documentLoaded) to ensure mobile FitWidth
              // completes BEFORE we lock zoom/scroll.
            } catch (e) {
              logger("log", "Could not set presentation mode");
            }
          }, 200);
        });
        // Disable UI elements but keep page navigation
        instance.UI.disableElements([
          "documentHeader",
          "header",
          "toolsHeader",
          "toolbar",
          "ribbon",
          "ribbons",
          "toolbarGroup-Edit",
          "toolbarGroup-Annotate",
          "toolbarGroup-Shapes",
          "toolbarGroup-Insert",
          "toolbarGroup-Forms",
          "toolbarGroup-Comment",
          "toolbarGroup-Print",
          "toolbarGroup-Download",
          "toolbarGroup-FillAndSign",
          "toolbarGroup-View",
          "toolbarGroup-Measure",
          "toolbarGroup-Zoom", // Keep zoom disabled, but allow navigation
          "snippingToolButton",
          "cropToolButton",
          "panToolButton",
          "selectToolButton",
          "viewControlsTab",
          "selectButton",
          "menuButton",
          "toolsButton",
          // Keep Navigation and Page controls enabled
        ]);

        // Try multiple methods to hide toolbar
        try {
          // Method 1: Set header items to empty
          if (instance.UI.setHeaderItems) {
            instance.UI.setHeaderItems(() => []);
          }
        } catch (e) {
          logger("log", "setHeaderItems not available");
        }

        try {
          // Method 2: Try to get and hide header element directly
          const headerElement = instance.UI.getHeader();
          if (headerElement) {
            headerElement.style.display = "none";
            headerElement.style.visibility = "hidden";
            headerElement.style.height = "0";
          }
        } catch (e) {
          logger("log", "getHeader not available");
        }

        try {
          // Method 3: Try to disable the entire UI
          if (instance.UI.disableFeatures) {
            instance.UI.disableFeatures(["header"]);
          }
        } catch (e) {
          logger("log", "disableFeatures not available");
        }
        // Set fit mode for presentation - fit page to window size automatically
        // Use FitWidth on mobile for better viewing experience
        const initialFitMode = getIsMobileView()
          ? instance.UI.FitMode.FitWidth
          : instance.UI.FitMode.FitPage;
        instance.UI.setFitMode(initialFitMode);
        const pageNumber = documentViewer.getCurrentPage();

        annotationManager.getAnnotationsList().forEach((annot) => {
          if (annot.PageNumber === pageNumber && annot.Color) {
            // Replace oklch (or any color) with safe RGB
            annot.Color = new Annotations.Color(0, 0, 0); // black
          }
        });

        logger("log", "documentViewer", documentViewer);

        // Listen for page rendering events to update page count (important for PPTX)
        documentViewer.addEventListener("pagesUpdated", () => {
          try {
            const pageCount = documentViewer.getPageCount();
            if (pageCount > 0) {
              setTotalPages(pageCount);
              logger("log", "Pages updated, new count:", pageCount);
            }
          } catch (e) {
            logger("error", "Error updating page count on pagesUpdated:", e);
          }
        });

        documentViewer.addEventListener(
          "pageNumberUpdated",
          async (pageNumber) => {
            // Allow scroll during page navigation
            isPageNavigating.current = true;

            // Update current page state
            setCurrentPage(pageNumber);

            // Also update total pages in case it wasn't available earlier (PPTX)
            try {
              const pageCount = documentViewer.getPageCount();
              if (pageCount > 0 && pageCount !== totalPages) {
                setTotalPages(pageCount);
              }
            } catch (e) {
              // Ignore errors
            }

            // Re-enable scroll locking after page navigation completes
            // Use longer timeout to ensure page is fully rendered and positioned
            setTimeout(() => {
              isPageNavigating.current = false;
              // Don't force scroll to 0 here - let PDFTron position the page naturally
              // The scroll lock interval will handle preventing user scrolling
            }, 1000); // Allow 1 second for page positioning and rendering

            // Don't broadcast if we're applying a remote change
            if (isApplyingRemoteChange.current) {
              return;
            }
            if (meetingRole != "moderator") {
              return;
            }

            logger("log", "pageNumber Updated - broadcasting", pageNumber);
            publish(
              JSON.stringify({
                eventName: "pageNumberUpdated",
                eventData: { pageNumber: pageNumber },
              }),
              { persist: true },
            );
          },
        );

        // Scroll (throttle) - DISABLED
        // const scrollView = documentViewer.getScrollViewElement();

        // scrollView.addEventListener("scroll", async (scroll) => {
        //   // Don't broadcast if we're applying a remote change
        //   if (isApplyingRemoteChange.current) {
        //     return;
        //   }

        //   const scrollTop = scrollView.scrollTop; // Y-axis
        //   const scrollLeft = scrollView.scrollLeft; // X-axis

        //   // Throttle scroll events to avoid flooding
        //   if (scrollTimeoutRef.current) {
        //     clearTimeout(scrollTimeoutRef.current);
        //   }

        //   scrollTimeoutRef.current = setTimeout(() => {
        //     if (meetingRole != "moderator") {
        //       return;
        //     }
        //     logger("log", "scroll Updated - broadcasting", scrollTop, scrollLeft);
        //     publish(
        //       JSON.stringify({
        //         eventName: "scrollUpdated",
        //         eventData: { scrollTop: scrollTop, scrollLeft: scrollLeft },
        //       }),
        //       { persist: true }
        //     );
        //   }, 200); // Throttle to 200ms for better stability
        // });

        // Zoom - DISABLED
        // documentViewer.addEventListener("zoomUpdated", (zoom) => {
        //   // Don't broadcast if we're applying a remote change
        //   if (isApplyingRemoteChange.current) {
        //     return;
        //   }
        //   if (meetingRole != "moderator") {
        //     return;
        //   }
        //   logger("log", "zoom Updated - broadcasting", zoom);
        //   publish(
        //     JSON.stringify({
        //       eventName: "zoomUpdated",
        //       eventData: { zoom: zoom },
        //     }),
        //     { persist: true }
        //   );
        // });

        documentViewer.addEventListener(
          "rotationUpdated",
          (rotation, pageNumber) => {
            logger("log", "rotationupdate triggered");
            let currentPage = documentViewer.getCurrentPage();
            logger("log", `Page ${currentPage} rotated to ${rotation}`);
            if (meetingRole != "moderator") {
              return;
            }
            publish(
              JSON.stringify({
                eventName: "rotationUpdated",
                eventData: { rotation: rotation, pageNumber: currentPage },
              }),
              { persist: true },
            );
          },
        );

        // Annotations
        annotationManager.addEventListener(
          "annotationChanged",
          async (annotations, action) => {
            if (meetingRole != "moderator") {
              return;
            }
            // Don't broadcast if we're applying a remote change
            if (isApplyingRemoteChange.current) {
              return;
            }
            const serialized = await annotationManager.exportAnnotations({
              links: false,
            });
            logger("log", "annotation Changed - broadcasting", action);
            publish(
              JSON.stringify({
                eventName: "annotationChanged",
                eventData: { annotations: serialized, action: action },
              }),
              { persist: true },
            );
          },
        );

        // Layout change
        documentViewer.addEventListener("displayModeUpdated", (displayMode) => {
          logger("log", "displayModeChanged triggered", displayMode);

          // Ensure display mode is maintained per device
          try {
            if (instance.UI.setDisplayMode) {
              instance.UI.setDisplayMode(
                getIsMobileView()
                  ? instance.UI.DisplayMode.Single
                  : instance.UI.DisplayMode.Continuous,
              );
            }
          } catch (e) {
            logger("log", "Could not set display mode");
          }

          if (meetingRole != "moderator") {
            return;
          }
          // Don't broadcast if we're applying a remote change
          if (isApplyingRemoteChange.current) {
            return;
          }
          // logger("log", "layout Changed - broadcasting", layoutMode);
          // publish(
          //   {
          //     eventName: "layoutChanged",
          //     eventData: { layoutMode: layoutMode },
          //   },
          //   { persist: true }
          // );
        });

        // Tool change
        instance.UI.addEventListener("toolModeUpdated", (tool) => {
          if (meetingRole != "moderator") {
            return;
          }
          // Don't broadcast if we're applying a remote change
          if (isApplyingRemoteChange.current) {
            return;
          }
          logger("log", "tool Changed - broadcasting", tool);
          publish(
            JSON.stringify({
              eventName: "toolModeUpdated",
              eventData: { toolName: tool.name || tool },
            }),
            { persist: true },
          );
        });

        // Fit mode
        instance.UI.addEventListener("fitModeChanged", (fitMode) => {
          // Lock zoom level after fit mode changes to prevent zoom changes
          setTimeout(() => {
            try {
              const lockedZoomLevel = instance.UI.getZoomLevel() || 1;
              const originalSetZoomLevel = instance.UI.setZoomLevel;
              if (originalSetZoomLevel && lockedZoomLevel) {
                originalSetZoomLevel.call(instance.UI, lockedZoomLevel);
              }
            } catch (e) {
              logger("log", "Could not lock zoom after fit mode change:", e);
            }
          }, 50);

          if (meetingRole != "moderator") {
            return;
          }
          // Don't broadcast if we're applying a remote change
          if (isApplyingRemoteChange.current) {
            return;
          }
          logger("log", "fitMode Changed - broadcasting", fitMode);
          publish(
            JSON.stringify({
              eventName: "fitModeChanged",
              eventData: { fitMode: fitMode },
            }),
            { persist: true },
          );
        });

        instance.UI.addEventListener("fullscreenchange", () => {
          logger("log", "fullscreenchange");
        });

        instance.UI.setLayoutMode(instance.UI.LayoutMode.Single);

        // Set display mode based on device
        try {
          instance.UI.setDisplayMode(
            getIsMobileView()
              ? instance.UI.DisplayMode.Single
              : instance.UI.DisplayMode.Continuous,
          );
        } catch (e) {
          logger("log", "setDisplayMode not available, using default");
        }

        // Desktop safety net: lock after instance creation.
        // Mobile is handled after FitWidth in documentLoaded.
        if (!getIsMobileView()) {
          setTimeout(() => {
            disableScrolling();
            disableZoom();
          }, 500);
        }

        // NOTE: zoom locking is enforced by disableZoom() after lockedZoomLevelRef
        // is captured. We intentionally don't force a zoom value here because on
        // mobile FitWidth must run first.

        // Function to update fit mode based on window size
        const updateFitMode = () => {
          try {
            // Use FitWidth on mobile, FitPage on desktop
            const fitMode = getIsMobileView()
              ? instance.UI.FitMode.FitWidth
              : instance.UI.FitMode.FitPage;
            instance.UI.setFitMode(fitMode);
            // Force update view to apply fit mode
            documentViewer.updateView();
          } catch (e) {
            logger("log", "Could not update fit mode");
          }
        };

        // Add window resize listener to auto-fit pages
        const handleResize = () => {
          updateFitMode();
        };
        resizeHandlerRef.current = handleResize;
        window.addEventListener("resize", handleResize);

        instance.UI.exportModularComponents();

        // Hide toolbars immediately and after multiple delays
        hideToolbars();
        setTimeout(hideToolbars, 100);
        setTimeout(hideToolbars, 300);
        setTimeout(hideToolbars, 500);
        setTimeout(hideToolbars, 1000);
        setTimeout(hideToolbars, 2000);

        // Use MutationObserver to hide dynamically added toolbar elements
        const observer = new MutationObserver(() => {
          hideToolbars();
        });

        if (viewer.current) {
          observer.observe(viewer.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["class", "data-element"],
          });
          observerRef.current = observer;
        }

        // Update both state and ref for the instance
        webviewerInstanceRef.current = instance;
        setWebViewerInstance(instance);
      });
    });

    // Cleanup function
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      // Remove resize listener
      if (resizeHandlerRef.current) {
        window.removeEventListener("resize", resizeHandlerRef.current);
        resizeHandlerRef.current = null;
      }
      // Clear zoom monitoring intervals
      if (
        window.documentViewerZoomMonitors &&
        Array.isArray(window.documentViewerZoomMonitors)
      ) {
        window.documentViewerZoomMonitors.forEach((intervalId) => {
          clearInterval(intervalId);
        });
        window.documentViewerZoomMonitors = [];
      }
      // Clear scroll lock intervals
      if (
        window.documentViewerScrollLocks &&
        Array.isArray(window.documentViewerScrollLocks)
      ) {
        window.documentViewerScrollLocks.forEach((intervalId) => {
          clearInterval(intervalId);
        });
        window.documentViewerScrollLocks = [];
      }
      // Reset document loaded flag
      isDocumentLoaded.current = false;
      queuedAnnotations.current = null;
    };
  }, [fileUrl]);

  // Inject CSS to force single page display on mobile
  useEffect(() => {
    const isMobileView =
      isMobile || (typeof window !== "undefined" && window.innerWidth <= 767);
    if (!isMobileView) return;

    const styleId = "mobile-single-page-style";
    let styleEl = document.getElementById(styleId);

    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    // Update CSS to hide all pages except current - more aggressive rules
    styleEl.textContent = `
      @media (max-width: 767px) {
        #document-viewer {
          overflow: hidden !important;
          width: 100% !important;
          display: block !important;
        }
        #document-viewer iframe {
          width: 100% !important;
          max-width: 100% !important;
          overflow: hidden !important;
        }
        #document-viewer [class*="DocumentContainer"],
        #document-viewer [class*="document-container"],
        #document-viewer [class*="Document"],
        #document-viewer [class*="document"] {
          width: 100% !important;
          max-width: 100% !important;
          overflow: hidden !important;
          display: block !important;
          flex-direction: column !important;
          columns: 1 !important;
          column-count: 1 !important;
        }
        /* Force single column - hide any multi-column layouts */
        #document-viewer [class*="column"],
        #document-viewer [class*="Column"],
        #document-viewer [style*="column"],
        #document-viewer [style*="columns"] {
          columns: 1 !important;
          column-count: 1 !important;
          display: block !important;
        }
        /* Hide all pages except current */
        #document-viewer [class*="page"],
        #document-viewer [class*="Page"] {
          width: 100% !important;
          max-width: 100% !important;
          display: block !important;
        }
        #document-viewer [class*="page"]:not(:nth-of-type(${currentPage})),
        #document-viewer [class*="Page"]:not(:nth-of-type(${currentPage})) {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          overflow: hidden !important;
          width: 0 !important;
        }
        #document-viewer [data-page-number]:not([data-page-number="${currentPage}"]) {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          overflow: hidden !important;
          width: 0 !important;
        }
        /* Hide any duplicate page containers and columns */
        #document-viewer > div > div > div:not(:nth-child(${currentPage})),
        #document-viewer > div > div:not(:first-child),
        #document-viewer > div:not(:first-child) {
          display: none !important;
        }
        /* Force single column layout - hide any column-based layouts */
        #document-viewer * {
          max-width: 100% !important;
          columns: 1 !important;
          column-count: 1 !important;
        }
        /* Specifically target any grid or flex layouts that might create columns */
        #document-viewer [style*="grid"],
        #document-viewer [style*="flex"],
        #document-viewer [class*="grid"],
        #document-viewer [class*="flex"] {
          display: block !important;
          flex-direction: column !important;
          grid-template-columns: 1fr !important;
        }
      }
    `;

    // Also try to directly hide pages and columns via DOM manipulation
    const hideDuplicatePages = () => {
      const viewerElement = viewer.current;
      if (!viewerElement) return;

      // Force single column layout on all containers
      const containers = viewerElement.querySelectorAll(
        '[class*="container"], [class*="Container"], div > div > div',
      );
      containers.forEach((container) => {
        container.style.display = "block";
        container.style.flexDirection = "column";
        container.style.columns = "1";
        container.style.columnCount = "1";
        container.style.width = "100%";
        container.style.maxWidth = "100%";
      });

      // Try to access iframe content
      const iframe = viewerElement.querySelector("iframe");
      if (iframe && iframe.contentDocument) {
        try {
          const iframeDoc = iframe.contentDocument;

          // Force single column in iframe
          const iframeContainers = iframeDoc.querySelectorAll(
            '[class*="container"], [class*="Container"], div',
          );
          iframeContainers.forEach((container) => {
            container.style.display = "block";
            container.style.flexDirection = "column";
            container.style.columns = "1";
            container.style.columnCount = "1";
            container.style.width = "100%";
            container.style.maxWidth = "100%";
          });

          // Hide duplicate pages
          const pages = iframeDoc.querySelectorAll(
            '[class*="page"], [data-page-number], [class*="Page"], [class*="column"], [class*="Column"]',
          );
          pages.forEach((page, index) => {
            const pageNum =
              page.getAttribute("data-page-number") ||
              page.getAttribute("data-page") ||
              index + 1;
            const isColumn =
              page.className &&
              (page.className.includes("column") ||
                page.className.includes("Column"));

            if (isColumn && parseInt(pageNum) !== currentPage) {
              page.style.display = "none";
              page.style.visibility = "hidden";
              page.style.height = "0";
              page.style.width = "0";
              page.style.overflow = "hidden";
            } else if (parseInt(pageNum) !== currentPage) {
              page.style.display = "none";
              page.style.visibility = "hidden";
              page.style.height = "0";
              page.style.overflow = "hidden";
            } else {
              page.style.display = "";
              page.style.visibility = "";
              page.style.height = "";
              page.style.width = "100%";
              page.style.overflow = "";
            }
          });
        } catch (e) {
          // Cross-origin or other error, ignore
        }
      }

      // Hide duplicate pages and columns in the main viewer element
      const allPages = viewerElement.querySelectorAll(
        '[class*="page"], [data-page-number], [class*="Page"], [class*="column"], [class*="Column"]',
      );
      allPages.forEach((page, index) => {
        const pageNum =
          page.getAttribute("data-page-number") ||
          page.getAttribute("data-page") ||
          index + 1;
        const isColumn =
          page.className &&
          (page.className.includes("column") ||
            page.className.includes("Column"));

        if (isColumn && parseInt(pageNum) !== currentPage) {
          page.style.display = "none";
          page.style.visibility = "hidden";
          page.style.width = "0";
        } else if (parseInt(pageNum) !== currentPage) {
          page.style.display = "none";
          page.style.visibility = "hidden";
        } else {
          page.style.width = "100%";
          page.style.maxWidth = "100%";
        }
      });

      // Hide any divs that might be creating columns (keep only first child)
      const potentialColumns =
        viewerElement.querySelectorAll("div > div > div");
      potentialColumns.forEach((col, index) => {
        if (index + 1 !== currentPage) {
          col.style.display = "none";
        } else {
          col.style.width = "100%";
          col.style.maxWidth = "100%";
        }
      });
    };

    // Run immediately and after delays to catch dynamically loaded pages
    hideDuplicatePages();
    setTimeout(hideDuplicatePages, 100);
    setTimeout(hideDuplicatePages, 500);
    setTimeout(hideDuplicatePages, 1000);
    setTimeout(hideDuplicatePages, 2000);

    return () => {
      // Cleanup on unmount
      const el = document.getElementById(styleId);
      if (el) {
        el.remove();
      }
    };
  }, [currentPage, isMobile]);

  const goToPreviousPage = () => {
    if (webviewerInstanceRef.current && currentPage > 1) {
      try {
        const { documentViewer } = webviewerInstanceRef.current.Core;
        const newPage = currentPage - 1;

        // Set flag to allow scroll during page navigation
        isPageNavigating.current = true;

        documentViewer.setCurrentPage(newPage);
        // Update state immediately for better UX
        setCurrentPage(newPage);

        // Re-enable scroll locking after page navigation completes
        // Use longer timeout to ensure page is fully rendered and positioned
        setTimeout(() => {
          isPageNavigating.current = false;
          // Don't force scroll to 0 here - let PDFTron position the page naturally
          // The scroll lock interval will handle preventing user scrolling
        }, 1000); // Allow 1 second for page positioning and rendering

        // Broadcast page change if moderator
        if (meetingRole === "moderator") {
          publish(
            JSON.stringify({
              eventName: "pageNumberUpdated",
              eventData: { pageNumber: newPage },
            }),
            { persist: true },
          );
        }
      } catch (e) {
        logger("error", "Error navigating to previous page:", e);
        isPageNavigating.current = false;
      }
    }
  };

  const goToNextPage = () => {
    if (webviewerInstanceRef.current && currentPage < totalPages) {
      try {
        const { documentViewer } = webviewerInstanceRef.current.Core;
        const newPage = currentPage + 1;

        // Set flag to allow scroll during page navigation
        isPageNavigating.current = true;

        documentViewer.setCurrentPage(newPage);
        // Update state immediately for better UX
        setCurrentPage(newPage);

        // Re-enable scroll locking after page navigation completes
        // Use longer timeout to ensure page is fully rendered and positioned
        setTimeout(() => {
          isPageNavigating.current = false;
          // Don't force scroll to 0 here - let PDFTron position the page naturally
          // The scroll lock interval will handle preventing user scrolling
        }, 1000); // Allow 1 second for page positioning and rendering

        // Broadcast page change if moderator
        if (meetingRole === "moderator") {
          publish(
            JSON.stringify({
              eventName: "pageNumberUpdated",
              eventData: { pageNumber: newPage },
            }),
            { persist: true },
          );
        }
      } catch (e) {
        logger("error", "Error navigating to next page:", e);
        isPageNavigating.current = false;
      }
    }
  };

  // DocumentViewer fills its container - parent handles layout adjustments
  // No need to calculate width as the middle section already adjusts via flex layout

  return (
    <div
      className="relative w-full h-full flex flex-col overflow-hidden"
      style={{ height: "100%", width: "100%" }}
    >
      {/* Close Button */}
      {meetingRole === "moderator" && (
        <div className="absolute top-[12px] right-[16px] z-[99]">
          <button
            onClick={() => onClosePanel()}
            className="p-2 rounded-full bg-white/90 backdrop-blur-sm shadow-lg active:bg-gray-200 hover:bg-gray-100 transition-colors duration-200 cursor-pointer z-[9] touch-manipulation"
            aria-label="Close document viewer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Custom Page Navigation Controls - Visible to everyone */}
      {meetingRole === "moderator" && totalPages > 0 && (
        <div
          className="absolute bottom-[20px] left-1/2 z-[100] flex items-center gap-4 bg-black/80 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg"
          style={{
            transform: "translateX(-50%)",
            touchAction: "manipulation",
            pointerEvents: "auto", // Ensure buttons are always clickable
          }}
        >
          <button
            onClick={goToPreviousPage}
            disabled={currentPage <= 1}
            className="p-2 rounded-full transition-all duration-200 touch-manipulation bg-white/20 active:bg-white/40 cursor-pointer disabled:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Previous page"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
          </button>

          <div className="flex items-center gap-2 text-white text-sm font-medium px-3">
            <span>{currentPage}</span>
            <span className="text-gray-400">/</span>
            <span>{totalPages}</span>
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            className="p-2 rounded-full transition-all duration-200 touch-manipulation bg-white/20 active:bg-white/40 cursor-pointer disabled:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next page"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 4.5l7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Document Viewer Container */}
      <div
        id="document-viewer"
        className="absolute inset-0 z-[9] overflow-hidden"
        style={{
          pointerEvents: meetingRole !== "moderator" ? "none" : "auto",
          width: "100%",
          height: "100%",
          touchAction: "none", // Disable all touch gestures including scrolling and zoom
          overflow: "hidden", // Prevent scrolling
          overflowY: "hidden", // Prevent vertical scrolling
          overflowX: "hidden", // Prevent horizontal scrolling
          WebkitOverflowScrolling: "auto",
          userSelect: "none", // Prevent text selection which might trigger zoom
          WebkitUserSelect: "none",
          msUserSelect: "none",
          zoom: "1", // Lock zoom level via CSS
          transform: "scale(1)", // Lock scale via transform
        }}
        ref={viewer}
      ></div>
    </div>
  );
}
